import { AmcLogo } from "@core/components/logo/amc-logo";
import { EditableFieldDef } from "@core/components/table/edit-fields";
import { RiskCell } from "@funds/components/risk-cell";
import { WatchlistStarCell } from "@funds/components/watchlist-star-cell";
import { Link } from "@tanstack/react-router";
import { Fund } from "creso-core/types";
import { formatAsCrore, formatAsPercentage } from "creso-core/utils";
import { formatDate } from "date-fns";
import { Badge, SortableHeader } from "web-design-system";
import { ProductTypeBadge } from "web-core";

export const ReturnsCell = ({ value }: { value: string }) => {
  if (!value) return "–";
  const percentage = Number(value).toFixed(2);
  const isPositive = Number(percentage) >= 0;

  if (isPositive) {
    return <span className="text-constructive">{percentage}%</span>;
  }

  return <span className="text-destructive">{percentage}%</span>;
};

// Rank images exist for ranks 1-40; higher ranks fall back to a plain tile
const MAX_RANK_IMAGE = 40;

// Block-level wrapper: an inline-flex Badge sits on the text baseline and
// renders slightly above the cell's vertical centre
const RankInCategoryCell = ({ fund }: { fund: Fund }) => (
  <div className="flex items-center">
    <RankInCategoryBadge fund={fund} />
  </div>
);

const RankInCategoryBadge = ({ fund }: { fund: Fund }) => {
  const rank = fund.ranking?.category_rank;

  if (fund.is_sunrise_fund) {
    return (
      <Badge
        variant="custom"
        className="bg-tag-slate text-tag-slate-foreground gap-2 p-0 pr-2"
      >
        <img
          className="size-6.5 shrink-0"
          src={`/assets/images/ranks/sunrise.webp`}
          alt="Sunrise funds"
        />
        Sunrise
      </Badge>
    );
  }

  if (!rank) {
    return (
      <Badge
        variant="custom"
        className="bg-tag-slate text-tag-slate-foreground px-2 py-1"
      >
        Unranked
      </Badge>
    );
  }
  return (
    <Badge
      variant="custom"
      className="bg-tag-slate text-tag-slate-foreground gap-2 p-0 pr-2"
    >
      {rank <= MAX_RANK_IMAGE ? (
        <img
          className="size-6.5 shrink-0"
          src={`/assets/images/ranks/rank-${rank}.webp`}
          alt={`Rank ${rank}`}
        />
      ) : (
        <span className="flex h-6.5 min-w-6.5 items-center justify-center rounded bg-tag-slate text-xs font-semibold text-tag-slate-foreground">
          {rank}
        </span>
      )}
      in category
    </Badge>
  );
};

// Columns whose id isn't the API's ordering field
export const FUND_SORT_FIELDS: Record<string, string> = {
  rank_in_category: "rankings__category_rank",
};

export const FUND_COLUMN_DEFAULT_VISIBILITY = {
  fund_name: true,
  rank_in_category: true,
  risk_level: false,
  expense_ratio: false,
  performance__return_1_month: false,
  performance__return_3_month: false,
  performance__return_6_month: false,
  performance__return_1_year: true,
  performance__return_3_year: true,
  performance__return_5_year: true,
  performance__return_10_year: false,
  performance__avg_rolling_return_1_year: false,
  performance__avg_rolling_return_3_year: false,
  performance__avg_rolling_return_5_year: false,
  performance__avg_rolling_return_10_year: false,
  assets_under_management: true,
  fund_level_category_name: true,
  inception_date: false,
  performance__return_ytd: false,
  performance__alpha_3_year: false,
  performance__beta_3_year: false,
  performance__sharpe_ratio_3_year: false,
  performance__sortino_ratio_3_year: false,
  performance__standard_deviation_3_year: false,
  watchlist: true,
};

export const columns: EditableFieldDef<Fund>[] = [
  {
    id: "fund_name",
    configurable: false,
    accessorKey: "legal_name",
    cell: ({ row }) => {
      return (
        <div className="flex sticky left-0 flex-row items-center gap-2">
          <AmcLogo amc_code={row.original.provider_company_id} />
          <div>
            <Link
              to={"/funds/$fund_id"}
              preloadDelay={1000}
              params={{ fund_id: row.original.fund_id }}
            >
              {row.original.legal_name}
            </Link>
            <ProductTypeBadge
              className="ml-2"
              type={row.original.product_type}
            />
          </div>
        </div>
      );
    },
    header: ({ column }) => (
      <SortableHeader column={column}>Fund Name</SortableHeader>
    ),
    minSize: 300,
    label: "Fund Name",
  },
  {
    id: "rank_in_category",
    header: ({ column }) => (
      <SortableHeader ascFirst column={column}>
        Rank
      </SortableHeader>
    ),
    accessorKey: "ranking.category_rank",
    cell: ({ row }) => <RankInCategoryCell fund={row.original} />,
    group: "General",
    label: "Rank",
    minSize: 150,
  },
  {
    id: "fund_level_category_name",
    header: "Fund Category",
    accessorKey: "fund_level_category_name",
    minSize: 150,
    cell: ({ row }) => (
      <Badge
        variant="custom"
        className="bg-tag-slate text-tag-slate-foreground px-2 py-1"
      >
        {row.original.fund_level_category_name}
      </Badge>
    ),
    group: "General",
    label: "Fund Category",
  },
  {
    id: "assets_under_management",
    accessorKey: "assets_under_management",
    cell: ({ row }) => (
      <span>{formatAsCrore(row.original.assets_under_management, 0)}</span>
    ),
    group: "General",
    label: "AUM",
    header: ({ column }) => (
      <SortableHeader column={column}>AUM</SortableHeader>
    ),
    minSize: 100,
  },
  {
    id: "expense_ratio",
    accessorKey: "expense_ratio",
    group: "General",
    label: "Expense Ratio",
    header: ({ column }) => (
      <SortableHeader column={column}>Expense Ratio</SortableHeader>
    ),
    cell: (info) => (
      <span>{formatAsPercentage(info.row.original.expense_ratio)}</span>
    ),
    meta: { className: "text-right pr-13" },
    minSize: 100,
  },
  {
    id: "performance__return_1_month",
    minSize: 100,
    header: ({ column }) => <SortableHeader column={column}>1M</SortableHeader>,
    accessorKey: "performance.return_1_month",
    cell: ({ row }) => (
      <ReturnsCell value={row.original.performance?.return_1_month} />
    ),
    group: "Returns",
    label: "1 Month trailing",
  },
  {
    id: "performance__return_3_month",
    minSize: 100,
    header: ({ column }) => <SortableHeader column={column}>3M</SortableHeader>,
    accessorKey: "performance.return_3_month",
    cell: ({ row }) => (
      <ReturnsCell value={row.original.performance?.return_3_month} />
    ),
    group: "Returns",
    label: "3 Month trailing",
  },
  {
    id: "performance__return_6_month",
    minSize: 100,
    header: ({ column }) => <SortableHeader column={column}>6M</SortableHeader>,
    accessorKey: "performance.return_6_month",
    cell: ({ row }) => (
      <ReturnsCell value={row.original.performance?.return_6_month} />
    ),
    group: "Returns",
    label: "6 Month trailing",
  },
  {
    id: "performance__return_1_year",
    minSize: 100,
    header: ({ column }) => <SortableHeader column={column}>1Y</SortableHeader>,
    accessorKey: "performance.return_1_year",
    cell: ({ row }) => (
      <ReturnsCell value={row.original.performance?.return_1_year} />
    ),
    group: "Returns",
    label: "1 Year trailing",
  },
  {
    id: "performance__return_3_year",
    minSize: 100,
    header: ({ column }) => <SortableHeader column={column}>3Y</SortableHeader>,
    accessorKey: "performance.return_3_year",
    cell: ({ row }) => (
      <ReturnsCell value={row.original.performance?.return_3_year} />
    ),
    group: "Returns",
    label: "3 Year trailing",
  },
  {
    id: "performance__return_5_year",
    minSize: 100,
    header: ({ column }) => <SortableHeader column={column}>5Y</SortableHeader>,
    accessorKey: "performance.return_5_year",
    cell: ({ row }) => (
      <ReturnsCell value={row.original.performance?.return_5_year} />
    ),
    group: "Returns",
    label: "5 Year trailing",
  },
  {
    id: "performance__return_10_year",
    minSize: 100,
    header: ({ column }) => (
      <SortableHeader column={column}>10Y</SortableHeader>
    ),
    accessorKey: "performance.return_10_year",
    cell: ({ row }) => (
      <ReturnsCell value={row.original.performance?.return_10_year} />
    ),
    group: "Returns",
    label: "10 Year trailing",
  },
  {
    id: "performance__return_ytd",
    minSize: 100,
    header: ({ column }) => (
      <SortableHeader column={column}>YTD</SortableHeader>
    ),
    accessorKey: "performance.return_ytd",
    cell: ({ row }) => (
      <ReturnsCell value={row.original.performance?.return_ytd} />
    ),
    group: "Returns",
    label: "YTD trailing",
  },
  {
    id: "performance__avg_rolling_return_1_year",
    minSize: 100,
    header: ({ column }) => (
      <SortableHeader column={column}>1Y rolling</SortableHeader>
    ),
    accessorKey: "performance.avg_rolling_return_1_year",
    cell: ({ row }) => (
      <ReturnsCell
        value={row.original.performance?.avg_rolling_return_1_year}
      />
    ),
    group: "Returns",
    label: "1 Year rolling",
  },
  {
    id: "performance__avg_rolling_return_3_year",
    minSize: 100,
    header: ({ column }) => (
      <SortableHeader column={column}>3Y rolling</SortableHeader>
    ),
    accessorKey: "performance.avg_rolling_return_3_year",
    cell: ({ row }) => (
      <ReturnsCell
        value={row.original.performance?.avg_rolling_return_3_year}
      />
    ),
    group: "Returns",
    label: "3 Year rolling",
  },
  {
    id: "performance__avg_rolling_return_5_year",
    minSize: 100,
    header: ({ column }) => (
      <SortableHeader column={column}>5Y rolling</SortableHeader>
    ),
    accessorKey: "performance.avg_rolling_return_5_year",
    cell: ({ row }) => (
      <ReturnsCell
        value={row.original.performance?.avg_rolling_return_5_year}
      />
    ),
    group: "Returns",
    label: "5 Year rolling",
  },
  {
    id: "performance__avg_rolling_return_10_year",
    minSize: 100,
    header: ({ column }) => (
      <SortableHeader column={column}>10Y rolling</SortableHeader>
    ),
    accessorKey: "performance.avg_rolling_return_10_year",
    cell: ({ row }) => (
      <ReturnsCell
        value={row.original.performance?.avg_rolling_return_10_year}
      />
    ),
    group: "Returns",
    label: "10 Year rolling",
  },
  {
    id: "risk_level",
    minSize: 150,
    header: "Risk",
    accessorKey: "risk_level",
    cell: ({ row }) => {
      return <RiskCell value={row.original.risk_level} />;
    },
    group: "General",
    label: "Risk",
  },
  {
    id: "inception_date",
    minSize: 100,
    accessorKey: "inception_date",
    cell: ({ row }) => (
      <span>
        {row.original.inception_date
          ? formatDate(new Date(row.original.inception_date), "dd MMM yyyy")
          : "-"}
      </span>
    ),
    group: "General",
    label: "Inception Date",
    header: ({ column }) => (
      <SortableHeader column={column}>Inception Date</SortableHeader>
    ),
  },
  {
    id: "performance__alpha_3_year",
    minSize: 100,
    header: ({ column }) => (
      <SortableHeader column={column}>Alpha</SortableHeader>
    ),
    accessorKey: "performance.alpha_3_year",
    cell: ({ row }) => (
      <span>{row.original.performance?.alpha_3_year ?? "-"}</span>
    ),
    group: "Performance",
    label: "Alpha",
  },
  {
    id: "performance__beta_3_year",
    minSize: 100,
    header: ({ column }) => (
      <SortableHeader column={column}>Beta</SortableHeader>
    ),
    accessorKey: "performance.beta_3_year",
    cell: ({ row }) => (
      <span>{row.original.performance?.beta_3_year ?? "-"}</span>
    ),
    group: "Performance",
    label: "Beta",
  },
  {
    id: "performance__sharpe_ratio_3_year",
    minSize: 100,
    accessorKey: "performance.sharpe_ratio_3_year",
    cell: ({ row }) => (
      <span>
        {Number(row.original.performance?.sharpe_ratio_3_year).toFixed(2) ??
          "-"}
      </span>
    ),
    group: "Performance",
    label: "Sharpe Ratio",
    header: ({ column }) => (
      <SortableHeader column={column}>Sharpe Ratio</SortableHeader>
    ),
  },
  {
    id: "performance__sortino_ratio_3_year",
    minSize: 100,
    header: ({ column }) => (
      <SortableHeader column={column}>Sortino Ratio</SortableHeader>
    ),
    accessorKey: "performance.sortino_ratio_3_year",
    cell: ({ row }) => (
      <span>
        {Number(row.original.performance?.sortino_ratio_3_year).toFixed(2) ??
          "-"}
      </span>
    ),
    group: "Performance",
    label: "Sortino Ratio",
  },
  {
    id: "performance__standard_deviation_3_year",
    minSize: 100,
    header: ({ column }) => (
      <SortableHeader column={column}>Standard Deviation</SortableHeader>
    ),
    accessorKey: "performance.standard_deviation_3_year",
    cell: ({ row }) => (
      <span>
        {Number(row.original.performance?.standard_deviation_3_year).toFixed(
          2
        ) ?? "-"}
      </span>
    ),
    group: "Performance",
    label: "Standard Deviation",
  },
  {
    id: "watchlist",
    configurable: false,
    label: "Watchlist",
    header: "",
    cell: ({ row }) => <WatchlistStarCell fundId={row.original.fund_id} />,
    enableSorting: false,
    meta: { className: "text-right" },
    size: 56,
    minSize: 56,
  },
];
